import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../data/supabaseClient';
import { useChat } from './ChatContext';
import { format } from "date-fns";
import { FaTrash, FaArchive, FaTimes } from "react-icons/fa";
import { CustomButton1, CustomButton10Ca, CustomButton2, CustomButton3, CustomButton4, CustomButton5, CloseButton2, Button } from '../Templates';
import Spinner from '../common/Spinner';
import { useTranslation } from 'react-i18next';
import './ChatV2.css';

function ConversationsList({ onSelect }) {
  const { toggleChat } = useChat();
  // Use both namespaces with default common
  const { t } = useTranslation(['common', 'chat']);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [canStartChat, setCanStartChat] = useState(true);
  const [chatLimits, setChatLimits] = useState(null);
  const [error, setError] = useState(null);
  const [isTogglingDetails, setIsTogglingDetails] = useState(new Set());
  const [visibleDetails, setVisibleDetails] = useState({});
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [animatingItems, setAnimatingItems] = useState(new Set());
  const conversationsWindow = useRef(null);
  const observerRef = useRef(null);
  const loadingRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Pagination settings
  const CONVERSATIONS_PER_PAGE = 10;

  const { 
    activeConversationId,
    unreadConversations,
    markConversationAsRead
  } = useChat();

  // Check user's chat limits using our consolidated function
  const checkChatLimits = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase.rpc(
        'get_user_chat_usage',
        { p_user_id: userId }
      );
      
      if (error) {
        console.error('Error checking chat limits:', error);
        return;
      }
      
      if (!data) {
        console.error('No data returned from get_user_chat_usage');
        return;
      }
      
      const limitData = {
        remaining: data.remaining_chats,
        max_allowed: data.monthly_limit,
        has_remaining: data.remaining_chats > 0 || data.monthly_limit >= 9999,
        monthly_count: data.monthly_count,
        plan_type: data.plan_type
      };
      
      setChatLimits(limitData);
      setCanStartChat(limitData.has_remaining);
    } catch (error) {
      console.error('Error checking chat limits:', error);
    }
  }, []);

  // Optimized conversation fetching with proper pagination
  const fetchConversations = async (userId, page = 0, isLoadMore = false) => {
    if (!userId) return;

    try {
      if (!isLoadMore) {
        setIsLoading(true);
        setError(null);
      } else {
        setIsLoadingMore(true);
      }
      
      console.log(`🔄 Fetching conversations page ${page} for user:`, userId);
      
      // Step 1: Get user's conversation IDs (all of them to check total)
      const { data: participantData, error: participantError } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', userId);
        
      if (participantError) {
        console.error('❌ Error fetching participant data:', participantError);
        throw participantError;
      }
      
      const allConversationIds = participantData?.map(p => p.conversation_id) || [];
      console.log(`📝 Found total conversation IDs:`, allConversationIds.length);
      
      if (allConversationIds.length === 0) {
        console.log('No conversations found for user');
        setConversations([]);
        setHasMoreConversations(false);
        return;
      }
      
      // Calculate pagination on conversation IDs
      const offset = page * CONVERSATIONS_PER_PAGE;
      const limit = CONVERSATIONS_PER_PAGE;
      
      // Get the subset of conversation IDs for this page and sort them by getting conversations first
      const { data: allConversationsData, error: sortError } = await supabase
        .from('conversations')
        .select('id, last_updated')
        .in('id', allConversationIds)
        .order('last_updated', { ascending: false });
        
      if (sortError) {
        console.warn('Could not sort conversations, using original order:', sortError);
        // Fallback to original logic if sorting fails
        var sortedConversationIds = allConversationIds;
      } else {
        // Use properly sorted conversation IDs
        var sortedConversationIds = allConversationsData.map(conv => conv.id);
      }
      
      const pageConversationIds = sortedConversationIds.slice(offset, offset + limit);
      
      // Check if there are more conversations after this page
      const remainingConversations = sortedConversationIds.length - offset - limit;
      const hasMore = remainingConversations > 0;
      
      console.log(`📄 Page ${page}: ${pageConversationIds.length} conversations, ${remainingConversations} remaining`);
      
      if (pageConversationIds.length === 0) {
        console.log('No conversations for this page');
        if (!isLoadMore) {
          setConversations([]);
        }
        setHasMoreConversations(false);
        return;
      }
      
      // Step 2: Fetch conversations data for this page
      const { data: conversationsData, error: convsError } = await supabase
        .from('conversations')
        .select(`
          id,
          title,
          last_message,
          last_updated,
          created_at,
          posts_id,
          package_id,
          occurrence_id,
          post_occurrences:occurrence_id (
            id,
            occurrence_date,
            occurrence_time
          ),
          posts:posts_id (
            id,
            map_image_url,
            address_from_street,
            address_from_city,
            address_to_street,
            address_to_city,
            role,
            travel_date,
            travel_time,
            travel_type,
            status,
            is_completed
          ),
          packages:package_id (
            id,
            map_image_url,
            address_from_street,
            address_from_city,
            address_to_street,
            address_to_city,
            package_size,
            travel_date,
            travel_time,
            delivery_urgency,
            status
          )
        `)
        .in('id', pageConversationIds)
        .order('last_updated', { ascending: false });
        
      // Step 2.1: Fetch rides data for post-related conversations
      const postRelatedConversations = conversationsData?.filter(conv => conv.posts_id) || [];
      const postIds = postRelatedConversations.map(conv => conv.posts_id);
      
      let ridesData = [];
      if (postIds.length > 0) {
        try {
          const { data: ridesResult, error: ridesError } = await supabase
            .from('rides')
            .select(`
              id,
              post_id,
              conversation_id,
              status,
              driver_id,
              passenger_id,
              reviews:reviews(id, reviewer_id)
            `)
            .in('post_id', postIds);
            
          if (ridesError) {
            console.warn('Error fetching rides data:', ridesError);
          } else {
            ridesData = ridesResult || [];
          }
        } catch (error) {
          console.warn('Failed to fetch rides data:', error);
        }
      }
      
      // Step 2.2: Fetch package deliveries data for package-related conversations
      const packageRelatedConversations = conversationsData?.filter(conv => conv.package_id) || [];
      const packageIds = packageRelatedConversations.map(conv => conv.package_id);
      
      let packageDeliveriesData = [];
      if (packageIds.length > 0) {
        try {
          const { data: deliveriesResult, error: deliveriesError } = await supabase
            .from('package_deliveries')
            .select(`
              id,
              package_id,
              conversation_id,
              status,
              sender_id,
              carrier_id
            `)
            .in('package_id', packageIds);
            
          if (deliveriesError) {
            console.warn('Error fetching package deliveries data:', deliveriesError);
          } else {
            packageDeliveriesData = deliveriesResult || [];
          }
        } catch (error) {
          console.warn('Failed to fetch package deliveries data:', error);
        }
      }
        
      // Step 3: Fetch all participants for these conversations
      const currentConversationIds = conversationsData?.map(c => c.id) || [];
      const { data: allParticipants, error: participantsError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          user_id,
          profiles (
            id,
            username,
            avatar_url
          )
        `)
        .in('conversation_id', currentConversationIds);

      if (convsError) {
        console.error('❌ Error fetching conversations:', convsError);
        
        if (convsError.code === '22P02') {
          console.error('SQL syntax error detected - likely UUID format issue');
          setError(t('error_conversations_load', { ns: 'chat' }));
          return;
        }
        
        if (convsError.code === 'PGRST116') {
          console.log('No conversations found for user (this is normal)');
          setConversations([]);
          setHasMoreConversations(false);
          return;
        }
        
        throw convsError;
      }
      
      if (participantsError) {
        console.error('❌ Error fetching participants:', participantsError);
        console.warn('Continuing without full participant data');
      }
      
      if (!conversationsData || conversationsData.length === 0) {
        if (!isLoadMore) {
          setConversations([]);
        }
        setHasMoreConversations(hasMore);
        return;
      }

      // Get hidden conversations for this user
      const { data: hiddenConversations, error: hiddenError } = await supabase
        .from('conversation_visibility')
        .select('conversation_id')
        .eq('user_id', userId)
        .eq('status', 'hidden');

      if (hiddenError) {
        console.warn('Error fetching hidden conversations:', hiddenError);
      }

      const hiddenIds = new Set(hiddenConversations?.map(h => h.conversation_id) || []);

      // Process the data to extract other participants and filter out hidden ones
      const processedConversations = conversationsData
        .filter(conv => {
          const isHidden = hiddenIds.has(conv.id);
          return !isHidden;
        })
        .map(conv => {
        
        // Find participants for this conversation
        const conversationParticipants = allParticipants?.filter(
          p => p.conversation_id === conv.id
        ) || [];
        
        // Find the other participant (not the current user)
        const otherParticipantData = conversationParticipants
          .find(p => p.user_id !== userId);
        
        // Extract profile information
        let otherParticipant = null;
        if (otherParticipantData?.profiles) {
          otherParticipant = {
            id: otherParticipantData.user_id,
            username: otherParticipantData.profiles.username,
            avatar_url: otherParticipantData.profiles.avatar_url
          };
        } else if (otherParticipantData) {
          otherParticipant = {
            id: otherParticipantData.user_id,
            username: null,
            avatar_url: null,
            needsFetch: true
          };
        }

        // Find associated rides for this conversation (with safety checks)
        // Prioritize rides that are directly linked to this conversation
        const associatedRides = (ridesData || []).filter(ride => {
          try {
            if (!ride) return false;
            
            // First priority: rides with matching conversation_id (most specific)
            if (ride.conversation_id && ride.conversation_id === conv.id) {
              return true;
            }
            
            // Second priority: rides with matching post_id but ONLY if no conversation_id exists
            // This prevents mixing rides from different conversations of the same post
            if (ride.post_id === conv.posts_id && !ride.conversation_id) {
              return true;
            }
            
            return false;
          } catch (error) {
            console.warn('Error filtering rides for conversation:', conv.id, error);
            return false;
          }
        });
        
        // Find associated package deliveries for this conversation (with safety checks)
        // Similar logic - prioritize conversation-specific deliveries
        const associatedDeliveries = (packageDeliveriesData || []).filter(delivery => {
          try {
            if (!delivery) return false;
            
            // First priority: deliveries with matching conversation_id (most specific)
            if (delivery.conversation_id && delivery.conversation_id === conv.id) {
              return true;
            }
            
            // Second priority: deliveries with matching package_id but ONLY if no conversation_id exists
            if (delivery.package_id === conv.package_id && !delivery.conversation_id) {
              return true;
            }
            
            return false;
          } catch (error) {
            console.warn('Error filtering deliveries for conversation:', conv.id, error);
            return false;
          }
        });

        return {
          ...conv,
          other_participant: otherParticipant,
          posts: conv.posts,
          package: conv.packages,
          rides: associatedRides,
          package_deliveries: associatedDeliveries
        };
      });
      
      console.log(`✅ Successfully fetched ${processedConversations.length} conversations for page ${page}`);
      
      // Debug log to check if we're getting ride data
      const conversationsWithRides = processedConversations.filter(conv => conv.rides && conv.rides.length > 0);
      const conversationsWithDeliveries = processedConversations.filter(conv => conv.package_deliveries && conv.package_deliveries.length > 0);
      console.log(`📊 Conversations with rides: ${conversationsWithRides.length}`);
      console.log(`📦 Conversations with deliveries: ${conversationsWithDeliveries.length}`);
      
      if (conversationsWithRides.length > 0) {
        console.log(`🚗 Conversations with rides: ${conversationsWithRides.length}`);
      }
      
      if (conversationsWithDeliveries.length > 0) {
        console.log(`📦 Conversations with deliveries: ${conversationsWithDeliveries.length}`);
      }
      
      // Set hasMoreConversations based on actual remaining conversations
      setHasMoreConversations(hasMore);
      
      // Check for conversations that need profile data fetching
      const conversationsNeedingProfiles = processedConversations.filter(
        conv => conv.other_participant?.needsFetch
      );
      
      if (conversationsNeedingProfiles.length > 0) {
        console.log('🔄 Fetching missing profile data for', conversationsNeedingProfiles.length, 'conversations');
        
        const userIds = conversationsNeedingProfiles.map(conv => conv.other_participant.id);
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, avatar_url')
          .in('id', userIds);
        
        if (!profilesError && profilesData) {
          processedConversations.forEach(conv => {
            if (conv.other_participant?.needsFetch) {
              const profile = profilesData.find(p => p.id === conv.other_participant.id);
              if (profile) {
                conv.other_participant = {
                  ...profile,
                  needsFetch: false
                };
              }
            }
          });
        }
      }
      
      // Update conversations with improved async handling
      if (isLoadMore) {
      // Filter out any conversations that might already exist to prevent duplicates
      const existingIds = new Set(conversations.map(c => c.id));
      const newConversations = processedConversations.filter(conv => !existingIds.has(conv.id));
      
      console.log(`📤 Adding ${newConversations.length} new conversations to existing ${conversations.length}`);
      
      // Add new conversations in batch to prevent key conflicts
      if (newConversations.length > 0) {
      setConversations(prev => {
        // Combine and sort all conversations by last_updated
        const combined = [...prev, ...newConversations];
        return combined.sort((a, b) => new Date(b.last_updated) - new Date(a.last_updated));
      });
      
      // Add animation effects
      newConversations.forEach((conv, index) => {
      setTimeout(() => {
      setAnimatingItems(prev => new Set([...prev, conv.id]));
      setTimeout(() => {
        setAnimatingItems(prev => {
          const newSet = new Set(prev);
            newSet.delete(conv.id);
              return newSet;
              });
              }, 300);
          }, index * 50);
        });
      }
      } else {
      // Initial load - set all conversations sorted by last_updated
      const sortedConversations = processedConversations.sort((a, b) => 
      new Date(b.last_updated) - new Date(a.last_updated)
      );
      setConversations(sortedConversations);
      
      // Add animation effects
      sortedConversations.forEach((conv, index) => {
      setTimeout(() => {
      setAnimatingItems(prev => new Set([...prev, conv.id]));
        setTimeout(() => {
            setAnimatingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(conv.id);
                return newSet;
              });
            }, 300);
          }, index * 50);
        });
      }
      
    } catch (error) {
      console.error('Error in fetchConversations:', error);
      setError(t('error_conversations_load', { ns: 'chat' }));
    } finally {
      if (!isLoadMore) {
        setIsLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  // Load more conversations function
  const loadMoreConversations = useCallback(() => {
    console.log('🔍 Load more called with state:', {
      hasUser: !!currentUser,
      hasMore: hasMoreConversations,
      isLoading: isLoadingMore,
      currentPage: currentPage
    });
    
    if (currentUser && hasMoreConversations && !isLoadingMore) {
      console.log('🔄 Loading more conversations - current page:', currentPage);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchConversations(currentUser.id, nextPage, true);
    } else {
      console.log('⏸️ Cannot load more - conditions not met');
    }
  }, [currentUser, hasMoreConversations, isLoadingMore, currentPage]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const setupObserver = () => {
      // Wait for scroll container to be available
      if (!scrollContainerRef.current) {
        console.log('⏳ Waiting for scroll container ref...');
        return;
      }
      
      console.log('🛠️ Setting up intersection observer with scroll container');
      
      const observer = new IntersectionObserver(
        (entries) => {
          const target = entries[0];
          console.log('🔍 Intersection observer triggered:', {
            isIntersecting: target.isIntersecting,
            hasMore: hasMoreConversations,
            isLoading: isLoadingMore,
            boundingRect: target.boundingClientRect
          });
          
          if (target.isIntersecting && hasMoreConversations && !isLoadingMore) {
            console.log('✅ Triggering load more');
            loadMoreConversations();
          }
        },
        {
          root: scrollContainerRef.current,
          rootMargin: '30px',
          threshold: 0.1
        }
      );

      observerRef.current = observer;

      if (loadingRef.current && hasMoreConversations) {
        console.log('👀 Observing loading element with scroll container');
        observer.observe(loadingRef.current);
      }
    };
    
    // Set up observer with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(setupObserver, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        console.log('🧹 Cleaning up observer');
        observerRef.current.disconnect();
      }
    };
  }, [loadMoreConversations, hasMoreConversations, isLoadingMore, conversations.length]);

  // Get current user and fetch data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setIsLoading(true);
        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (error) throw error;
        
        setCurrentUser(user);
        
        if (user) {
          await fetchConversations(user.id, 0, false);
          await checkChatLimits(user.id);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setError(t('error_user_fetch', { ns: 'chat' }));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCurrentUser();
  }, [t, checkChatLimits]);

  // Subscribe to message changes
  useEffect(() => {
    if (!currentUser) return;

    const messageChannel = supabase
      .channel('message-changes')
      .on('postgres_changes', 
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          const conversationId = payload.new.conversation_id;
          console.log('New message detected:', payload);

          // Update conversation order when new message arrives
          setConversations(prev => {
            const existingConvIndex = prev.findIndex(conv => conv.id === conversationId);
            if (existingConvIndex !== -1) {
              // Move conversation to top and update last_updated
              const updatedConversations = [...prev];
              const conversationToMove = {
                ...updatedConversations[existingConvIndex],
                last_updated: new Date().toISOString(),
                last_message: payload.new.content || updatedConversations[existingConvIndex].last_message
              };
              
              // Remove from current position and add to top
              updatedConversations.splice(existingConvIndex, 1);
              updatedConversations.unshift(conversationToMove);
              
              return updatedConversations;
            }
            return prev;
          });

          try {
            const { data: visibilityRecord, error: visibilityError } = await supabase
              .from('conversation_visibility')
              .select('*')
              .eq('user_id', currentUser.id)
              .eq('conversation_id', conversationId)
              .eq('status', 'hidden')
              .maybeSingle();

            if (visibilityError && visibilityError.code !== 'PGRST116') {
              console.error('Error checking visibility:', visibilityError);
              return;
            }

            if (visibilityRecord) {
              const { data: isParticipant, error: participantError } = await supabase
                .from('conversation_participants')
                .select('user_id')
                .eq('conversation_id', conversationId)
                .eq('user_id', currentUser.id)
                .maybeSingle();

              if (participantError && participantError.code !== 'PGRST116') {
                console.error('Error checking participant:', participantError);
                return;
              }

              if (isParticipant) {
                const { error: updateError } = await supabase
                  .from('conversation_visibility')
                  .update({ 
                    status: 'visible',
                    updated_at: new Date().toISOString() 
                  })
                  .eq('user_id', currentUser.id)
                  .eq('conversation_id', conversationId);

                if (updateError) {
                  console.error('Error updating visibility:', updateError);
                  return;
                }

                // Reset and reload conversations
                setConversations([]);
                setCurrentPage(0);
                setHasMoreConversations(true);
                fetchConversations(currentUser.id, 0, false);
              }
            }
          } catch (error) {
            console.error('Error handling message subscription:', error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messageChannel);
    };
  }, [currentUser]);

  // Subscribe to user stats changes
  useEffect(() => {
    if (!currentUser) return;
    
    const statsChannel = supabase
      .channel('user-stats-changes')
      .on('postgres_changes', 
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_stats',
          filter: `user_id=eq.${currentUser.id}`
        },
        () => {
          console.log('User stats updated, refreshing chat limits');
          checkChatLimits(currentUser.id);
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(statsChannel);
    };
  }, [currentUser, checkChatLimits]);
  
  // Listen for chat count updates with custom event
  useEffect(() => {
    const handleChatCountUpdated = (event) => {
      console.log('Chat count updated event received in ConversationsList:', event.detail);
      if (event.detail && (event.detail.remaining !== undefined)) {
        setChatLimits(prev => ({
          ...prev,
          remaining: event.detail.remaining,
          max_allowed: event.detail.limit || prev?.max_allowed,
          monthly_count: event.detail.count || prev?.monthly_count,
          plan_type: prev?.plan_type || 'free'
        }));
        setCanStartChat(event.detail.remaining > 0 || (event.detail.limit >= 9999));
      }
    };
    
    window.addEventListener('chatCountUpdated', handleChatCountUpdated);
    
    return () => {
      window.removeEventListener('chatCountUpdated', handleChatCountUpdated);
    };
  }, []);

  const handleConversationClick = useCallback((conversationId) => {
    onSelect(conversationId);
    markConversationAsRead(conversationId);
  }, [onSelect, markConversationAsRead]);

  const handleDeleteConversation = useCallback(async (conversationId) => {
    try {
      setIsDeleting(conversationId);
      
      const { data: existingRecord, error: selectError } = await supabase
        .from('conversation_visibility')
        .select('*')
        .eq('conversation_id', conversationId)
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (selectError && selectError.code !== 'PGRST116') {
        throw selectError;
      }

      if (existingRecord) {
        const { error: updateError } = await supabase
          .from('conversation_visibility')
          .update({ 
            status: 'hidden',
            updated_at: new Date().toISOString() 
          })
          .eq('conversation_id', conversationId)
          .eq('user_id', currentUser.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('conversation_visibility')
          .insert({
            conversation_id: conversationId,
            user_id: currentUser.id,
            status: 'hidden',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) throw insertError;
      }

      // Animate out and remove
      setConversations((prev) => 
        prev.filter((conv) => conv.id !== conversationId)
      );
    } catch (error) {
      console.error('Error managing conversation visibility:', error);
      alert(t('error_delete_conversation', { ns: 'chat' }));
    } finally {
      setIsDeleting(null);
    }
  }, [currentUser]);

  const toggleDetailsVisibility = useCallback((conversationId) => {
    // Prevent rapid clicking
    if (isTogglingDetails.has(conversationId)) {
      return;
    }
    
    setIsTogglingDetails(prev => new Set([...prev, conversationId]));
    
    setVisibleDetails(prev => {
      const currentState = prev[conversationId] || false;
      const newState = !currentState;
      
      return {
        ...prev,
        [conversationId]: newState
      };
    });
    
    // Remove from toggling set after a short delay
    setTimeout(() => {
      setIsTogglingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(conversationId);
        return newSet;
      });
    }, 300);
  }, [isTogglingDetails]);
  
  // Helper functions for formatting (memoized)
  const formatTime = useCallback((time) => time?.split(':').slice(0, 2).join(':') || t('not_specified', { ns: 'posts' }), [t]);
  const formatDate = useCallback((date) => date ? format(new Date(date), "dd/MM/yyyy") : t('not_specified', { ns: 'posts' }), [t]);
  const formatAddress = useCallback((street, city) => [street, city].filter(Boolean).join(", ") || t('not_specified', { ns: 'posts' }), [t]);
  
  // Helper function to check if conversation should show archived status
  const isConversationInactive = useCallback((convo) => {
    try {
      // Safety check for conversation object
      if (!convo) {
        return false;
      }
      
      // Simple logic: only show archived if post/package is inactive
      // Individual ride/delivery statuses don't matter as long as post is active
      
      if (convo.posts && typeof convo.posts === 'object') {
        const post = convo.posts;
        const isPostInactive = post.status !== 'active' || post.is_completed === true;
        console.log(`📝 Post status check for ${convo.id}: status='${post.status}', completed='${post.is_completed}' -> ${isPostInactive ? 'ARCHIVED' : 'ACTIVE'}`);
        return isPostInactive;
      }
      
      if (convo.package && typeof convo.package === 'object') {
        const pkg = convo.package;
        const isPackageInactive = pkg.status !== 'active';
        console.log(`📦 Package status check for ${convo.id}: status='${pkg.status}' -> ${isPackageInactive ? 'ARCHIVED' : 'ACTIVE'}`);
        return isPackageInactive;
      }
      
      console.log(`✅ No post/package found for ${convo.id} - defaulting to ACTIVE`);
      
    } catch (error) {
      console.warn('Error checking conversation inactive status:', error, {
        convId: convo?.id,
        hasPost: !!(convo?.posts),
        hasPackage: !!(convo?.package)
      });
    }
    
    return false;
  }, []);
  
  // Helper function to get status info (text and type)
  const getStatusInfo = useCallback((convo) => {
    try {
      // Safety check for conversation object
      if (!convo) {
        console.warn('getStatusInfo called with null/undefined conversation');
        return null;
      }
      
      // Simple logic: only show "archived" status when post/package is inactive
      // Individual ride/delivery actions don't determine status anymore
      
      if (convo.posts && typeof convo.posts === 'object') {
        const post = convo.posts;
        if (post.is_completed === true) {
          console.log(`📝 Post ${convo.id} is completed -> ARCHIVED`);
          return {
            text: t('status.archived', { ns: 'chat' }),
            type: 'archived'
          };
        }
        if (post.status && post.status !== 'active') {
          console.log(`📝 Post ${convo.id} not active (${post.status}) -> ARCHIVED`);
          return {
            text: t('status.archived', { ns: 'chat' }),
            type: 'archived'
          };
        }
      }
      
      if (convo.package && typeof convo.package === 'object') {
        const pkg = convo.package;
        if (pkg.status && pkg.status !== 'active') {
          console.log(`📦 Package ${convo.id} not active (${pkg.status}) -> ARCHIVED`);
          return {
            text: t('status.archived', { ns: 'chat' }),
            type: 'archived'
          };
        }
      }
      
      // If we reach here, the conversation is active (no status to show)
      console.log(`✅ Conversation ${convo.id} is ACTIVE - no status to show`);
      
    } catch (error) {
      console.warn('Error getting conversation status info:', error, {
        convId: convo?.id,
        hasPost: !!(convo?.posts),
        hasPackage: !!(convo?.package)
      });
    }
    
    return null;
  }, [t]);

  // Handle retry on error
  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    setConversations([]);
    setCurrentPage(0);
    setHasMoreConversations(true);
    
    if (currentUser) {
      fetchConversations(currentUser.id, 0, false);
    } else {
      const fetchCurrentUser = async () => {
        try {
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          if (userError) throw userError;
          setCurrentUser(user);
        } catch (error) {
          console.error('Error re-fetching user:', error);
          setError(t('error_user_fetch', { ns: 'chat' }));
          setIsLoading(false);
        }
      };
      fetchCurrentUser();
    }
  };

  const renderConversation = useCallback((convo) => {
    const isUnread = unreadConversations.has(convo.id);
    const isActive = convo.id === activeConversationId;
    const isBeingDeleted = isDeleting === convo.id;
    const isAnimating = animatingItems.has(convo.id);
    const otherParticipant = convo.other_participant;
    const hasMessage = convo.last_message && convo.last_message.trim().length > 0;
    const isInactive = isConversationInactive(convo);
    const statusInfo = getStatusInfo(convo);

    // Debug logging for specific conversation
    console.log(`🔍 CONVERSATION ${convo.id}: post=${convo.posts?.status}, package=${convo.package?.status}, isInactive=${isInactive}, status=${statusInfo?.text || 'none'}, user=${otherParticipant?.username}`);

    return (
      <div
        key={convo.id}
        className={`conversation-item ${isUnread ? 'unread' : ''} ${isActive ? 'active' : ''} ${isAnimating ? 'conversation-animating' : ''} ${isInactive ? 'inactive' : ''}`}
      >
        <div className="conversation-main-content">
          <div 
            className="participant-info"
            onClick={() => handleConversationClick(convo.id)}
          >
            <div className="avatar-container">
              {otherParticipant?.avatar_url ? (
                <img
                  src={otherParticipant.avatar_url}
                  alt={t('profile_picture', { ns: 'chat' })}
                  className="avatar-img"
                />
              ) : (
                <div className="avatar-placeholder">
                  {(otherParticipant?.username?.charAt(0) || otherParticipant?.id?.charAt(0) || '?').toUpperCase()}
                </div>
              )}
              {isUnread && <div className="conversation-dot dot-message" />}
            </div>
            
            <div className="conversation-details">
              <div className="conversation-header">
                <div className="username-with-status">
                  <p className="username">
                    {otherParticipant?.username || `${t('unknown_user', { ns: 'chat' })} ${otherParticipant?.id?.slice(-4) || '????'}`}
                  </p>
                  {isInactive && statusInfo && (
                    <div className="status-indicator">
                      <FaArchive className="status-icon archived" title={statusInfo.text} />
                      <span className="status-text">{statusInfo.text}</span>
                    </div>
                  )}
                </div>
                {convo.last_updated && (
                  <span className="convo-time">
                    {format(new Date(convo.last_updated), "HH:mm")}
                  </span>
                )}
              </div>
              {hasMessage && (
                <p className="last-message">
                  {convo.last_message.length > 35 
                    ? `${convo.last_message.substring(0, 35)}...` 
                    : convo.last_message}
                </p>
              )}
            </div>
          </div>
          
          <div className="conversation-actions">
            <CustomButton10Ca
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(t('confirm_delete_conversation', { ns: 'chat' }))) {
                  handleDeleteConversation(convo.id);
                }
              }}
              disabled={isBeingDeleted}
            >
              {isBeingDeleted ? (
                <Spinner size="small" showText={false} thickness={2} />
              ) : (
                <FaTrash size={14} />
              )}
            </CustomButton10Ca>
          </div>
        </div>

        <div className="conversation-secondary-content">
          <button
            className="toggle-details-btn"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleDetailsVisibility(convo.id);
            }}
          >
            {visibleDetails[convo.id] ? t('buttons.close') : t('buttons.details')}
          </button>
  
          {/* Render package details if this is a package conversation */}
          {convo.package && visibleDetails[convo.id] && (
            <div className="details-container">
              {convo.package.map_image_url && (
                <div className="map-image-container">
                  <img
                    src={convo.package.map_image_url}
                    alt={t('route_map', { ns: 'chat' })}
                    className="route-map-image"
                  />
                </div>
              )}
              <div className="route-details">
                <p>
                  <strong>{t('posts.address_from')}: </strong>
                  {formatAddress(convo.package.address_from_street, convo.package.address_from_city)}
                </p>
                <p>
                  <strong>{t('posts.address_to')}: </strong>
                  {formatAddress(convo.package.address_to_street, convo.package.address_to_city)}
                </p>
                <p className="travel-time">
                  <span className="travel-date">{formatDate(convo.package.travel_date)}</span> {t('at', { ns: 'posts' })} <span className="travel-hour">{formatTime(convo.package.travel_time)}</span>
                </p>
                <div className="travel-type-indicators">
                  <span className="travel-role-badge">{t('posts.package')}</span>
                  <span className="travel-type-badge">{convo.package.package_size || t('posts.not_specified')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Render post details if this is a post conversation */}
          {convo.posts && visibleDetails[convo.id] && (
            <div className="details-container">
              {convo.posts.map_image_url && (
                <div className="map-image-container">
                  <img
                    src={convo.posts.map_image_url}
                    alt={t('route_map', { ns: 'chat' })}
                    className="route-map-image"
                  />
                </div>
              )}
              <div className="route-details">
                <p>
                  <strong>{t('posts.address_from')}: </strong>
                  {formatAddress(convo.posts.address_from_street, convo.posts.address_from_city)}
                </p>
                <p>
                  <strong>{t('posts.address_to')}: </strong>
                  {formatAddress(convo.posts.address_to_street, convo.posts.address_to_city)}
                </p>
                <p className="travel-time">
                  {/* Show occurrence date/time if available (for recurring posts), otherwise show post date/time */}
                  <span className="travel-date">
                    {convo.post_occurrences?.occurrence_date
                      ? formatDate(convo.post_occurrences.occurrence_date)
                      : formatDate(convo.posts.travel_date)
                    }
                  </span> {t('at', { ns: 'posts' })} <span className="travel-hour">
                    {convo.post_occurrences?.occurrence_time
                      ? formatTime(convo.post_occurrences.occurrence_time)
                      : formatTime(convo.posts.travel_time)
                    }
                  </span>
                </p>
                <div className="travel-type-indicators">
                  <span className="travel-role-badge">{t(`${convo.posts.role?.toLowerCase()}`, { ns: 'posts' })}</span>
                  <span className="travel-type-badge">{t(`${convo.posts.travel_type?.toLowerCase().replace('-', '_')}`, { ns: 'posts' })}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, [unreadConversations, activeConversationId, isDeleting, animatingItems, toggleDetailsVisibility, handleConversationClick, handleDeleteConversation, formatTime, formatDate, formatAddress, isConversationInactive, getStatusInfo, t]);

  // Handle error state
  if (error) {
    return (
      <div className="conversations-list-error">
        <p>{error}</p>
        <Button
          variant="primary"
          onClick={handleRetry}
        >
          {t('buttons.retry')}
        </Button>
      </div>
    );
  }

  // Handle loading state
  if (isLoading) {
    return (
      <div className="conversations-list-loading">
        <Spinner size="medium" customText={t('loading_conversations', { ns: 'chat' })} />
      </div>
    );
  }

  return (
    <div className="conversations-list">
      {/* Chat Header with Close Button */}
      <div className="chat-header-conversations">
        <h3 className="chat-header-title">{t('my_active_conversations', { ns: 'chat' })}</h3>
        <CloseButton2
          size="small"
          onClick={toggleChat}
          className="chat-close-button"
        />
      </div>
      
      {/* Chat Limits Info Section */}
      <div className="chat-limit-section">
        {chatLimits && (
          <div className="chat-limits-container">
          <p className={`chat-limit-info ${chatLimits.max_allowed >= 9999 ? 'unlimited' : (chatLimits.remaining <= 2 ? 'warning' : '')}`}>
            {chatLimits.max_allowed >= 9999 ? 
              t('unlimited_chats', { ns: 'chat' }) : 
              t('chats_remaining', { count: chatLimits.remaining, ns: 'chat' })}
            {chatLimits.next_reset_date && chatLimits.max_allowed < 9999 && (
              <span className="reset-date-info">
                {t('resets_on', { date: new Date(chatLimits.next_reset_date).toLocaleDateString(), ns: 'chat' })}  
              </span>
            )}
          </p>
            
            {!canStartChat && (
              <div className="limit-reached-message">
                <p>{t('limit_reached_message', { max: chatLimits?.max_allowed || 5, ns: 'chat' })}</p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/subscription')}
                >
                  {t('view_subscription_plans', { ns: 'chat' })}
                </Button>
              </div>
            )}
            
            {canStartChat && chatLimits.remaining <= 2 && chatLimits.max_allowed < 9999 && (
              <div className="limit-reached-message">
                <p>{t('low_chat_remaining_message', { ns: 'chat' })}</p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/subscription')}
                >
                  {t('upgrade_now', { ns: 'chat' })}
                </Button>
              </div>
            )}
          </div>
        )}
        {!chatLimits && (
        <p className="chat-limit-info">
        {t('loading_limits', { ns: 'chat' })}
        </p>
        )}
      </div>
      
      {/* Conversations List */}
      {conversations.length > 0 ? (
        <div className="conversations-scrollable" ref={scrollContainerRef}>
          {conversations.map(renderConversation)}
          
          {/* Loading indicator and infinite scroll trigger */}
          {hasMoreConversations && (
            <div ref={loadingRef} className="load-more-container">
              {isLoadingMore ? (
                <div className="load-more-spinner">
                  <Spinner size="small" showText={false} />
                </div>
              ) : (
                <div className="load-more-trigger">
                  <p style={{ fontSize: '12px', color: 'var(--secondary-color)', textAlign: 'center' }}>
                    Scroll to load more...
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* End of list indicator */}
          {!hasMoreConversations && conversations.length > CONVERSATIONS_PER_PAGE && (
            <div className="end-of-list">
              <p>{t('all_conversations_loaded', { ns: 'chat' })}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="no-conversations">
          <p>{t('no_conversations', { ns: 'chat' })}</p>
        </div>
      )}
    </div>
  );
}

export default ConversationsList;